type CarouselOptions = {
  track: HTMLElement;
  prevButton?: HTMLButtonElement | null;
  nextButton?: HTMLButtonElement | null;
  itemSelector: string;
  gap?: number;
};

/**
 * Service 캐러셀
 * - 클릭당 카드 1장(+gap)만 이동
 * - 마지막에 남은 카드가 뷰포트에 맞으면 정지 (무한 루프 없음)
 */
export function initServiceCarousel({
  track,
  prevButton,
  nextButton,
  itemSelector,
  gap = 30,
}: CarouselOptions): () => void {
  const cards = () =>
    Array.from(track.querySelectorAll<HTMLElement>(itemSelector)).filter(
      (el) => !el.classList.contains('is-clone'),
    );

  if (cards().length === 0) return () => undefined;

  track.querySelectorAll(`${itemSelector}.is-clone`).forEach((node) => node.remove());

  let index = 0;
  let animating = false;

  const getGap = () => {
    const styles = window.getComputedStyle(track);
    const raw = Number.parseFloat(styles.columnGap || styles.gap);
    return Number.isFinite(raw) ? raw : gap;
  };

  const getCardWidth = () => {
    const first = cards()[0];
    if (!first) return 0;
    return first.getBoundingClientRect().width;
  };

  /** 한 칸 = 카드 1장 + gap */
  const getStep = () => {
    const width = getCardWidth();
    if (width <= 0) return 0;
    return width + getGap();
  };

  const getViewportWidth = () => {
    const wrap = track.closest('.service__track-wrap') ?? track.parentElement;
    if (!wrap) return window.innerWidth;
    const styles = window.getComputedStyle(wrap);
    const padding =
      (Number.parseFloat(styles.paddingLeft) || 0) +
      (Number.parseFloat(styles.paddingRight) || 0);
    return Math.max(0, wrap.clientWidth - padding);
  };

  /** 화면에 보이는 카드 수 (최소 1) */
  const getVisibleCount = () => {
    const step = getStep();
    if (step <= 0) return 1;
    const viewport = getViewportWidth();
    return Math.max(1, Math.floor((viewport + getGap()) / step));
  };

  const getMaxIndex = () => {
    const total = cards().length;
    if (total <= 0) return 0;
    return Math.max(0, total - getVisibleCount());
  };

  const syncButtons = () => {
    const maxIndex = getMaxIndex();
    if (prevButton) {
      prevButton.disabled = index <= 0;
      prevButton.classList.toggle('is-disabled', index <= 0);
    }
    if (nextButton) {
      nextButton.disabled = index >= maxIndex;
      nextButton.classList.toggle('is-disabled', index >= maxIndex);
    }
  };

  const setPosition = (nextIndex: number, animate: boolean) => {
    const step = getStep();
    const offset = Math.max(0, nextIndex) * step;

    track.style.transition = animate ? 'transform 0.45s ease' : 'none';
    track.style.transform = `translate3d(${-offset}px, 0, 0)`;
  };

  const goNext = () => {
    if (animating) return;
    const maxIndex = getMaxIndex();
    if (index >= maxIndex) return;

    animating = true;
    index += 1;
    setPosition(index, true);
    syncButtons();
  };

  const goPrev = () => {
    if (animating) return;
    if (index <= 0) return;

    animating = true;
    index -= 1;
    setPosition(index, true);
    syncButtons();
  };

  const onTransitionEnd = (event: TransitionEvent) => {
    if (event.target !== track || event.propertyName !== 'transform') return;
    animating = false;
    syncButtons();
  };

  const onResize = () => {
    index = Math.min(index, getMaxIndex());
    setPosition(index, false);
    animating = false;
    syncButtons();
  };

  setPosition(0, false);
  syncButtons();

  track.addEventListener('transitionend', onTransitionEnd);
  prevButton?.addEventListener('click', goPrev);
  nextButton?.addEventListener('click', goNext);
  window.addEventListener('resize', onResize);

  return () => {
    track.removeEventListener('transitionend', onTransitionEnd);
    prevButton?.removeEventListener('click', goPrev);
    nextButton?.removeEventListener('click', goNext);
    window.removeEventListener('resize', onResize);
    track.style.transform = '';
    track.style.transition = '';
    if (prevButton) {
      prevButton.disabled = false;
      prevButton.classList.remove('is-disabled');
    }
    if (nextButton) {
      nextButton.disabled = false;
      nextButton.classList.remove('is-disabled');
    }
  };
}

export type BrandSlide = {
  name: string;
  description: string;
  image: string;
  visual: string;
  alt: string;
};

export function initBrandSlider(
  root: ParentNode,
  slides: BrandSlide[],
): () => void {
  if (slides.length === 0) return () => undefined;

  const cardTrack = root.querySelector<HTMLElement>('[data-brand-card-track]');
  const visualTrack = root.querySelector<HTMLElement>('[data-brand-visual-track]');
  const nameEl = root.querySelector<HTMLElement>('[data-brand-name]');
  const descEl = root.querySelector<HTMLElement>('[data-brand-desc]');
  const prev = root.querySelector<HTMLButtonElement>('[data-brand-prev]');
  const next = root.querySelector<HTMLButtonElement>('[data-brand-next]');

  const count = slides.length;
  // [clone(last), ...slides, clone(first)] — 무한 루프에서도 이미지가 이어짐
  let position = 1; // 실제 첫 슬라이드
  let animating = false;
  let fallbackTimer = 0;

  const slideAt = (loopIndex: number): BrandSlide => {
    if (loopIndex === 0) return slides[count - 1]!;
    if (loopIndex === count + 1) return slides[0]!;
    return slides[loopIndex - 1]!;
  };

  const realIndex = () => {
    if (position === 0) return count - 1;
    if (position === count + 1) return 0;
    return position - 1;
  };

  const renderCopy = () => {
    const slide = slides[realIndex()];
    if (!slide) return;
    if (nameEl) nameEl.textContent = slide.name;
    if (descEl) descEl.textContent = slide.description;
  };

  const buildTracks = () => {
    const loopCount = count + 2;

    if (cardTrack) {
      cardTrack.replaceChildren();
      for (let i = 0; i < loopCount; i += 1) {
        const slide = slideAt(i);
        const img = document.createElement('img');
        img.src = slide.image;
        img.alt = i === 1 ? slide.alt : '';
        img.width = 400;
        img.height = 400;
        img.loading = i <= 2 ? 'eager' : 'lazy';
        img.draggable = false;
        cardTrack.appendChild(img);
      }
    }

    if (visualTrack) {
      visualTrack.replaceChildren();
      for (let i = 0; i < loopCount; i += 1) {
        const slide = slideAt(i);
        const img = document.createElement('img');
        img.src = slide.visual;
        img.alt = '';
        img.width = 960;
        img.height = 900;
        img.loading = i <= 2 ? 'eager' : 'lazy';
        img.draggable = false;
        visualTrack.appendChild(img);
      }
    }
  };

  const setTracks = (pos: number, instant: boolean) => {
    const apply = (track: HTMLElement | null, axis: 'x' | 'y') => {
      if (!track) return;
      track.classList.toggle('is-instant', instant);
      if (axis === 'y') {
        track.style.transform = `translate3d(0, ${-pos * 100}%, 0)`;
      } else {
        track.style.transform = `translate3d(${-pos * 100}%, 0, 0)`;
      }
      if (instant) {
        void track.offsetHeight;
        track.classList.remove('is-instant');
      }
    };

    apply(cardTrack, 'y');
    apply(visualTrack, 'x');
  };

  const normalizeIfClone = () => {
    if (position === 0) {
      position = count;
      setTracks(position, true);
    } else if (position === count + 1) {
      position = 1;
      setTracks(position, true);
    }
  };

  const finishMove = () => {
    if (!animating) return;
    window.clearTimeout(fallbackTimer);
    normalizeIfClone();
    animating = false;
    renderCopy();
  };

  const onTrackEnd = (event: TransitionEvent) => {
    if (event.propertyName !== 'transform') return;
    if (event.target !== event.currentTarget) return;
    finishMove();
  };

  const move = (delta: 1 | -1) => {
    if (animating || count < 2) return;
    animating = true;
    position += delta;
    setTracks(position, false);
    renderCopy();

    window.clearTimeout(fallbackTimer);
    fallbackTimer = window.setTimeout(finishMove, 750);
  };

  let autoplayTimer = 0;
  const stopAutoplay = () => {
    window.clearInterval(autoplayTimer);
    autoplayTimer = 0;
  };
  const startAutoplay = () => {
    stopAutoplay();
    if (count < 2) return;
    autoplayTimer = window.setInterval(() => {
      move(1);
    }, 3000);
  };

  buildTracks();
  setTracks(position, true);
  renderCopy();

  // transitionend는 한쪽만 듣고, 나머지는 같은 transform 타이밍으로 동기화
  const endTrack = cardTrack ?? visualTrack;
  endTrack?.addEventListener('transitionend', onTrackEnd);

  const onPrev = () => {
    move(-1);
    startAutoplay();
  };
  const onNext = () => {
    move(1);
    startAutoplay();
  };

  prev?.addEventListener('click', onPrev);
  next?.addEventListener('click', onNext);
  startAutoplay();

  return () => {
    stopAutoplay();
    window.clearTimeout(fallbackTimer);
    prev?.removeEventListener('click', onPrev);
    next?.removeEventListener('click', onNext);
    endTrack?.removeEventListener('transitionend', onTrackEnd);
  };
}
